import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Elements } from '@stripe/react-stripe-js';
import cx from 'classnames';

import Typography from '~/components/Typography';
import useSubscriberCheckout from '~/hooks/useSubscriberCheckout';
import type { Publication } from '~/interfaces/publication';
import type { PricingDetails } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';

import GiftCards from './components/GiftCards';
import GiftForm from './components/GiftForm';

interface Props {
  publication: Publication;
  upgradePage: UpgradePage;
  giftPricingDetails: PricingDetails;
}

const GiftTab = ({ publication, upgradePage, giftPricingDetails }: Props) => {
  const { handleRedirectAfterPayment, stripeSession, isExternalCheckout, priceId, setPriceId } = useSubscriberCheckout({
    publication,
    upgradePage,
    pricingDetails: giftPricingDetails,
  });
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);

  return (
    <Elements stripe={stripeSession}>
      {showForm ? (
        <GiftForm
          onCancel={() => setShowForm(false)}
          selectedPriceId={priceId}
          upgradePage={upgradePage}
          publication={publication}
          pricingDetails={giftPricingDetails}
          isExternalCheckoutEnabled={isExternalCheckout}
        />
      ) : (
        <>
          <Typography
            as="div"
            size={upgradePage.main_section.description_font_size}
            color={upgradePage.main_section.description_color}
            weight="regular"
            fontFamily={upgradePage.main_section.description_font_family}
            className={cx({
              'w-full max-w-2xl text-center': true,
            })}
          >
            {t('upgrade.tabs.gift.description', { name: publication.name })}
          </Typography>
          <GiftCards
            priceId={priceId}
            setPriceId={setPriceId}
            upgradePage={upgradePage}
            pricingDetails={giftPricingDetails}
            isExternalCheckoutEnabled={isExternalCheckout}
            handleRedirectAfterPayment={handleRedirectAfterPayment}
            onContinue={() => setShowForm(true)}
          />
        </>
      )}
    </Elements>
  );
};

export default GiftTab;
